export function todayRange() {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return [date, new Date(date)];
}
export function validPeriod(range) {
  return Array.isArray(range) && range.every((date) => date instanceof Date && Number.isFinite(date.getTime()))
    && range.length === 2 && range[0] <= range[1];
}
export function periodBounds(range) {
  if (!validPeriod(range)) throw new Error('Invalid period');
  const start = new Date(range[0]);
  start.setHours(0, 0, 0, 0);
  const end = new Date(range[1]);
  end.setHours(0, 0, 0, 0);
  end.setDate(end.getDate() + 1);
  return [start.getTime(), end.getTime() - 1];
}
export function periodDays(range) {
  if (!validPeriod(range)) return 0;
  const calendar = (date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.round((calendar(range[1]) - calendar(range[0])) / 86400000) + 1;
}
export function previousPeriod(range) {
  if (!validPeriod(range)) return previousPeriod(todayRange());
  const end = new Date(range[0]);
  end.setHours(0, 0, 0, 0);
  end.setDate(end.getDate() - 1);
  const start = new Date(end);
  start.setDate(start.getDate() - periodDays(range) + 1);
  return [start, end];
}
export const difference = (a, b) => ({ a, b, delta: b - a, percent: a === 0 ? null : (b - a) / Math.abs(a) * 100 });
export function summaryRevenue(summary = {}, method = 'all') {
  if (method && method !== 'all') return Number(summary[`${method}Amount`]) || 0;
  return ['cash', 'card', 'qr', 'bankTransfer'].reduce((total, name) => total + Math.round((Number(summary[`${name}Amount`]) || 0) * 100), 0) / 100;
}
function groupProducts(summary = {}) {
  const groups = new Map();
  for (const product of summary.products || []) {
    const category = product.category || 'Other';
    const name = product.title || '—';
    const key = JSON.stringify([name, category]);
    const entry = groups.get(key) || { key, name, category, customIcon: null, quantity: 0, revenue: 0, revenueHT: 0 };
    if (!entry.customIcon && product.customIcon) entry.customIcon = product.customIcon;
    entry.quantity += Number(product.quantity) || 0;
    entry.revenue += Number(product.totalDue) || 0;
    entry.revenueHT += Number(product.revenueHT ?? (
      (Number(product.totalIn) || 0) / (1 + (Number(product.vat) || 0) / 100)
      + (Number(product.totalOut) || 0) / (1 + (Number(product.vatOut) || 0) / 100)
    )) || 0;
    groups.set(key, entry);
  }
  for (const entry of groups.values()) {
    entry.revenue = Math.round(entry.revenue * 100) / 100;
    entry.revenueHT = Math.round(entry.revenueHT * 100) / 100;
  }
  return groups;
}
const moneyDifference = (a, b) => ({ ...difference(a, b), delta: Math.round((b - a) * 100) / 100 });
export function compareSummaries(a = {}, b = {}, method = 'all') {
  const left = groupProducts(a), right = groupProducts(b);
  const productKeys = [...new Set([...left.keys(), ...right.keys()])];
  const products = productKeys.map((key) => {
    const first = left.get(key), second = right.get(key), identity = first || second;
    return { key, name: identity.name, category: identity.category, customIcon: second?.customIcon || first?.customIcon || null, isNew: !first && !!second,
      quantity: difference(first?.quantity || 0, second?.quantity || 0),
      revenue: moneyDifference(first?.revenue || 0, second?.revenue || 0),
      revenueHT: moneyDifference(first?.revenueHT || 0, second?.revenueHT || 0) };
  }).sort((x, y) => x.name.localeCompare(y.name));
  const categoryKeys = [...new Set(products.map((product) => product.category))];
  const productTotalA = products.reduce((sum, line) => sum + line.revenue.a, 0);
  const productTotalB = products.reduce((sum, line) => sum + line.revenue.b, 0);
  const categories = categoryKeys.map((name) => {
    const lines = products.filter((product) => product.category === name);
    const revenue = moneyDifference(lines.reduce((sum, line) => sum + line.revenue.a, 0), lines.reduce((sum, line) => sum + line.revenue.b, 0));
    const shareA = productTotalA === 0 ? null : revenue.a / productTotalA * 100;
    const shareB = productTotalB === 0 ? null : revenue.b / productTotalB * 100;
    return { name, revenue, shareA, shareB, shareDelta: shareA === null || shareB === null ? null : shareB - shareA };
  }).sort((x, y) => x.name.localeCompare(y.name));
  return { revenue: moneyDifference(summaryRevenue(a, method), summaryRevenue(b, method)),
    quantity: difference(products.reduce((sum, line) => sum + line.quantity.a, 0), products.reduce((sum, line) => sum + line.quantity.b, 0)),
    categories, products };
}
